import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Loader2,
  Sparkles,
  TriangleAlert,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui";
import type { UIChecklistItem } from "@/packages/types";
import { useTranslation } from "@/packages/utils";

type PipelineStage = NonNullable<UIChecklistItem["fullPipelineStage"]>;
type PipelineStatus = NonNullable<UIChecklistItem["fullPipelineStatus"]>;
type StepState = "pending" | "active" | "completed" | "failed";

interface WorkflowCardVisibilityOptions {
  allowIdlePrompt?: boolean;
  canGenerateEverything?: boolean;
  isBusy?: boolean;
}

const STEP_ORDER: PipelineStage[] = [
  "answering_questions",
  "generating_findings",
  "generating_summary",
  "enriching_findings",
];

export function hasChecklistWorkflowContext(item: UIChecklistItem): boolean {
  return !!item.fullPipelineStage && !!item.fullPipelineStatus;
}

export function shouldShowChecklistWorkflowCard(
  item: UIChecklistItem,
  options: WorkflowCardVisibilityOptions = {},
): boolean {
  const hasSummary = !!item.summaryReport;

  if (hasChecklistWorkflowContext(item)) {
    return (
      item.fullPipelineStatus === "running" ||
      item.fullPipelineStatus === "failed" ||
      (item.fullPipelineStatus === "completed" && !hasSummary)
    );
  }

  if (
    !options.allowIdlePrompt ||
    !options.canGenerateEverything ||
    options.isBusy
  ) {
    return false;
  }

  const hasLinkedDocs = (item.linkedDocumentIds?.length ?? 0) > 0;
  const questionCount = item.questions?.length ?? 0;
  const answeredCount = item.answers?.length ?? 0;
  const hasUnansweredQuestions = answeredCount < questionCount;

  return hasLinkedDocs && (hasUnansweredQuestions || !hasSummary);
}

function extractSummaryPreview(summaryReport?: string): string | null {
  if (!summaryReport) {
    return null;
  }

  try {
    const parsed = JSON.parse(summaryReport) as Record<string, unknown>;

    if (
      typeof parsed.verdict === "string" &&
      parsed.verdict.trim().length > 0
    ) {
      return parsed.verdict.trim();
    }

    if (Array.isArray(parsed.keyFindings)) {
      const findings = parsed.keyFindings
        .map((entry) => {
          if (
            entry !== null &&
            typeof entry === "object" &&
            "finding" in entry &&
            typeof entry.finding === "string"
          ) {
            return entry.finding.trim();
          }
          return null;
        })
        .filter((entry): entry is string => !!entry);

      if (findings.length > 0) {
        return findings.join(" ");
      }
    }
  } catch {
    // Fallback to raw text preview below.
  }

  const cleaned = summaryReport.replace(/\s+/g, " ").trim();
  return cleaned.length > 0 ? cleaned : null;
}

function getActiveStepIndex(stage?: PipelineStage): number {
  if (!stage) {
    return -1;
  }

  return STEP_ORDER.indexOf(stage);
}

function getStepState(
  step: PipelineStage,
  activeStepIndex: number,
  status?: PipelineStatus,
): StepState {
  const stepIndex = STEP_ORDER.indexOf(step);

  if (activeStepIndex === -1 || !status) {
    return "pending";
  }

  if (status === "completed") {
    return "completed";
  }

  if (status === "failed") {
    if (stepIndex < activeStepIndex) {
      return "completed";
    }
    if (stepIndex === activeStepIndex) {
      return "failed";
    }
    return "pending";
  }

  if (stepIndex < activeStepIndex) {
    return "completed";
  }
  if (stepIndex === activeStepIndex) {
    return "active";
  }
  return "pending";
}

function getProgressPercent(item: UIChecklistItem): number {
  const stage = item.fullPipelineStage;
  const status = item.fullPipelineStatus;

  if (!stage || !status) {
    return 0;
  }

  if (status === "completed") {
    return 100;
  }

  const activeStepIndex = getActiveStepIndex(stage);
  const baseProgress = Math.max(activeStepIndex, 0) * 25;

  if (stage === "answering_questions") {
    const totalQuestions = item.questions?.length ?? 0;
    const answeredQuestions = Math.min(
      item.answers?.length ?? 0,
      totalQuestions,
    );
    const questionProgress =
      totalQuestions > 0 ? answeredQuestions / totalQuestions : 0.5;
    return Math.round(baseProgress + questionProgress * 25);
  }

  return Math.round(baseProgress + 12.5);
}

function StepStatusIcon({ state }: { state: StepState }) {
  if (state === "completed") {
    return <CheckCircle2 size={16} className="text-green-600" />;
  }

  if (state === "active") {
    return <Loader2 size={16} className="animate-spin text-accent" />;
  }

  if (state === "failed") {
    return <TriangleAlert size={16} className="text-destructive" />;
  }

  return <span className="block size-2 rounded-full bg-border" />;
}

function StepStateBadge({ state }: { state: StepState }) {
  const { t } = useTranslation("audits");

  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
        state === "active"
          ? "bg-accent/10 text-accent"
          : state === "completed"
            ? "bg-green-500/10 text-green-700 dark:text-green-400"
            : state === "failed"
              ? "bg-destructive/10 text-destructive"
              : "bg-page text-muted"
      }`}
    >
      {t(`checklist.ai.workflow.stepStates.${state}`)}
    </span>
  );
}

function ResultStatusTile({
  state,
  title,
  detail,
}: {
  state: "completed" | "pending";
  title: string;
  detail: string;
}) {
  return (
    <div
      className={`rounded-lg border px-3 py-3 ${
        state === "completed"
          ? "border-green-500/20 bg-green-500/6"
          : "border-amber-500/20 bg-amber-500/6"
      }`}
    >
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 shrink-0">
          <StepStatusIcon
            state={state === "completed" ? "completed" : "pending"}
          />
        </div>
        <div className="min-w-0">
          <p className="text-[12px] font-medium text-primary">{title}</p>
          <p className="mt-1 text-[11px] leading-snug text-muted">{detail}</p>
        </div>
      </div>
    </div>
  );
}

function StepRow({
  item,
  step,
  state,
  compact = false,
}: {
  item: UIChecklistItem;
  step: PipelineStage;
  state: StepState;
  compact?: boolean;
}) {
  const { t } = useTranslation("audits");
  const stepKey = `checklist.ai.workflow.steps.${step}` as const;
  const answerCount = Math.min(
    item.answers?.length ?? 0,
    item.questions?.length ?? 0,
  );
  const questionCount = item.questions?.length ?? 0;

  const detail =
    step === "answering_questions"
      ? t(`${stepKey}.detail`, {
          answered: answerCount,
          total: questionCount,
        })
      : t(`${stepKey}.detail`);

  return (
    <div
      className={`flex rounded-lg border transition-colors ${
        compact
          ? "items-center gap-2 px-2.5 py-1.5"
          : "items-start gap-3 px-3 py-2.5"
      } ${
        state === "active"
          ? "border-accent/30 bg-accent/6"
          : state === "completed"
            ? "border-green-500/20 bg-green-500/6"
            : state === "failed"
              ? "border-destructive/20 bg-destructive/6"
              : "border-border/70 bg-page/60"
      }`}
    >
      <div
        className={`flex shrink-0 items-center justify-center ${
          compact ? "h-4 w-4" : "mt-0.5 h-5 w-5"
        }`}
      >
        <StepStatusIcon state={state} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[12px] font-medium text-primary">
            {t(`${stepKey}.title`)}
          </p>
          {!compact && <StepStateBadge state={state} />}
        </div>
        {!compact && (
          <p className="text-[11px] leading-snug text-muted">{detail}</p>
        )}
      </div>
      {compact && <StepStateBadge state={state} />}
    </div>
  );
}

export function ChecklistGenerationWorkflowCard({
  item,
  onGenerateEverything,
}: {
  item: UIChecklistItem;
  onGenerateEverything?: () => void;
}) {
  const { t } = useTranslation("audits");
  const activeStage = item.fullPipelineStage;
  const status = item.fullPipelineStatus;
  const [isCollapsed, setIsCollapsed] = useState(() => status === "completed");
  const activeStepIndex = getActiveStepIndex(activeStage);
  const progressPercent = getProgressPercent(item);
  const totalQuestions = item.questions?.length ?? 0;
  const answeredCount = Math.min(item.answers?.length ?? 0, totalQuestions);
  const summaryPreview = extractSummaryPreview(item.summaryReport);

  const isIdle = !activeStage || !status;
  const isRunning = status === "running";
  const isCompleted = status === "completed";
  const isFailed = status === "failed";
  const hasSummary = !!summaryPreview;
  const findingsReady = item.findingsGenerationStatus === "completed";
  const answersReady = totalQuestions === 0 || answeredCount >= totalQuestions;
  const hasConfirmedOutputs = hasSummary && findingsReady;
  const completedNeedsAttention = isCompleted && !hasConfirmedOutputs;
  const canRerunEverything =
    !!onGenerateEverything && (isIdle || isFailed || isCompleted);
  const rerunEverythingLabel = isCompleted
    ? t("checklist.ai.workflow.rerunEverything")
    : t("checklist.ai.generateEverything");

  useEffect(() => {
    if (isCompleted) {
      setIsCollapsed(true);
    }
  }, [isCompleted]);

  const showBody = !isCollapsed;
  const shellClass = isCollapsed ? "p-3" : "p-4";

  const title = isIdle
    ? t("checklist.ai.workflow.idleTitle")
    : isRunning
      ? t("checklist.ai.workflow.runningTitle")
      : isCompleted
        ? completedNeedsAttention
          ? t("checklist.ai.workflow.completedNeedsAttentionTitle")
          : t("checklist.ai.workflow.completedTitle")
        : t("checklist.ai.workflow.failedTitle");

  const description = isIdle
    ? t("checklist.ai.workflow.idleDescription")
    : isRunning
      ? t("checklist.ai.workflow.runningDescription")
      : isCompleted
        ? completedNeedsAttention
          ? t("checklist.ai.workflow.completedNeedsAttentionDescription")
          : t("checklist.ai.workflow.completedDescription")
        : t("checklist.ai.workflow.failedDescription", {
            step: activeStage
              ? t(`checklist.ai.workflow.steps.${activeStage}.title`)
              : t("checklist.ai.generateEverything"),
          });

  const headerToneClass = isFailed
    ? "text-destructive"
    : completedNeedsAttention
      ? "text-amber-700 dark:text-amber-400"
      : isCompleted
        ? "text-green-700 dark:text-green-400"
        : "text-accent";

  return (
    <div
      className={`rounded-xl border border-border/80 bg-card/80 shadow-sm ${shellClass}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-accent/10">
            {isFailed ? (
              <TriangleAlert size={18} className="text-destructive" />
            ) : isRunning ? (
              <Loader2 size={18} className="animate-spin text-accent" />
            ) : completedNeedsAttention ? (
              <TriangleAlert size={18} className="text-amber-600" />
            ) : isCompleted ? (
              <CheckCircle2 size={18} className="text-green-600" />
            ) : (
              <Sparkles size={18} className="text-accent" />
            )}
          </div>
          <div className="min-w-0">
            <p
              className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${headerToneClass}`}
            >
              {t("checklist.ai.generateEverything")}
            </p>
            <p className="text-[14px] font-semibold text-primary">{title}</p>
            {showBody && (
              <p className="text-[12px] leading-relaxed text-muted">
                {description}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 self-start">
          {canRerunEverything && (
            <Button
              variant={isCompleted ? "secondary" : "primary"}
              size="sm"
              icon={Sparkles}
              onClick={onGenerateEverything}
              className="shrink-0"
            >
              {rerunEverythingLabel}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={() => setIsCollapsed((value) => !value)}
            aria-label={
              isCollapsed
                ? t("checklist.ai.workflow.expandDetails")
                : t("checklist.ai.workflow.collapseDetails")
            }
            className="shrink-0"
          >
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${
                isCollapsed ? "rotate-0" : "rotate-180"
              }`}
            />
          </Button>
        </div>
      </div>

      {showBody && !isIdle && (
        <>
          <div className="mt-4 flex items-center justify-between gap-3 text-[11px]">
            <span className="font-medium text-secondary">
              {t("checklist.ai.workflow.progressLabel")}
            </span>
            <span className="font-mono text-muted tabular-nums">
              {progressPercent}%
            </span>
          </div>

          <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-accent/10">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                isFailed
                  ? "bg-destructive"
                  : isCompleted
                    ? "bg-green-500"
                    : "bg-accent"
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <div
            className={`mt-4 grid gap-2 ${isCompleted ? "sm:grid-cols-2" : ""}`}
          >
            {STEP_ORDER.map((step) => (
              <StepRow
                key={step}
                item={item}
                step={step}
                state={getStepState(step, activeStepIndex, status)}
                compact={isCompleted}
              />
            ))}
          </div>

          {isCompleted && (
            <div className="mt-4 rounded-xl border border-border/70 bg-page/70 p-3.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
                    {t("checklist.ai.workflow.resultsTitle")}
                  </p>
                  <p className="text-[12px] leading-relaxed text-muted">
                    {completedNeedsAttention
                      ? t("checklist.ai.workflow.resultsAttentionDescription")
                      : t("checklist.ai.workflow.resultsDescription")}
                  </p>
                </div>
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                <ResultStatusTile
                  state={answersReady ? "completed" : "pending"}
                  title={t("checklist.ai.workflow.outputs.answersTitle")}
                  detail={
                    totalQuestions > 0
                      ? t("checklist.ai.workflow.outputs.answersDetail", {
                          answered: answeredCount,
                          total: totalQuestions,
                        })
                      : t("checklist.ai.workflow.outputs.answersEmpty")
                  }
                />
                <ResultStatusTile
                  state={findingsReady ? "completed" : "pending"}
                  title={
                    findingsReady
                      ? t("checklist.ai.workflow.outputs.findingsReadyTitle")
                      : t("checklist.ai.workflow.outputs.findingsPendingTitle")
                  }
                  detail={
                    findingsReady
                      ? t("checklist.ai.workflow.outputs.findingsReadyDetail")
                      : t("checklist.ai.workflow.outputs.findingsPendingDetail")
                  }
                />
                <ResultStatusTile
                  state={hasSummary ? "completed" : "pending"}
                  title={
                    hasSummary
                      ? t("checklist.ai.workflow.outputs.summaryReadyTitle")
                      : t("checklist.ai.workflow.outputs.summaryPendingTitle")
                  }
                  detail={
                    hasSummary
                      ? t("checklist.ai.workflow.outputs.summaryReadyDetail")
                      : t("checklist.ai.workflow.outputs.summaryPendingDetail")
                  }
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function ChecklistSummaryPreviewCard({
  item,
  onOpenSummaryTab,
}: {
  item: UIChecklistItem;
  onOpenSummaryTab?: () => void;
}) {
  const { t } = useTranslation("audits");
  const previewText = extractSummaryPreview(item.summaryReport);
  const isPending = item.summaryStatus === "pending" && !previewText;
  const isFailed = item.summaryStatus === "failed" && !previewText;
  const isClickable = !!previewText && !!onOpenSummaryTab;

  const handleClick = () => {
    if (isClickable) {
      onOpenSummaryTab?.();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!isClickable}
      aria-label={
        isClickable
          ? t("checklist.ai.openAiTab")
          : t("checklist.ai.summaryPreviewTitle")
      }
      className={`group relative block w-full overflow-hidden rounded-xl border border-accent/15 bg-gradient-to-br from-accent/[0.07] via-accent/[0.02] to-transparent p-4 text-left transition-all ${
        isClickable
          ? "cursor-pointer hover:border-accent/35 hover:from-accent/[0.11] hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          : "cursor-default"
      }`}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-accent/15 blur-2xl"
      />

      <div className="relative flex items-start gap-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent/10 ring-1 ring-inset ring-accent/20">
          <Sparkles size={13} className="text-accent" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">
              {t("checklist.ai.summaryPreviewTitle")}
            </span>
            {isPending && (
              <Loader2
                size={11}
                className="shrink-0 animate-spin text-accent/70"
              />
            )}
          </div>

          <div className="mt-1.5">
            {previewText ? (
              <p className="line-clamp-2 text-[13px] font-medium leading-relaxed text-primary">
                {previewText}
              </p>
            ) : isPending ? (
              <p className="text-[12px] leading-relaxed text-muted">
                {t("checklist.ai.summaryPreviewPending")}
              </p>
            ) : isFailed ? (
              <div className="flex items-start gap-1.5 text-[12px] leading-relaxed text-muted">
                <TriangleAlert
                  size={12}
                  className="mt-0.5 shrink-0 text-destructive"
                />
                <span>{t("checklist.ai.summaryPreviewFailed")}</span>
              </div>
            ) : (
              <p className="text-[12px] leading-relaxed text-muted">
                {t("checklist.ai.summaryPreviewEmpty")}
              </p>
            )}
          </div>
        </div>

        {isClickable && (
          <div className="flex shrink-0 items-center gap-1 self-center text-[11px] font-medium text-muted transition-colors group-hover:text-accent">
            <span>{t("checklist.ai.openAiTab")}</span>
            <ArrowRight
              size={12}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </div>
        )}
      </div>
    </button>
  );
}
